/**
 * night_sky_schedule — the PURE deterministic scheduler behind the living
 * night sky (station transits, ambient satellites, meteors, and the
 * fleet-battle-seen-from-the-ground). Every client computes the identical
 * schedule from the wall clock (the weather/war-clock house pattern:
 * `Date.now()/1000` + an FNV-1a hash — no server state, no RNG).
 *
 * CPU-side only: SkyModule drives shader uniforms from these each frame; the
 * TSL layer (lib/sky/night_live.ts) just draws dots/streaks where told. That
 * split keeps ALL scheduling Node-testable (verify:night-sky) and the
 * fragment shader trivial.
 *
 * Realism anchors (2026-08-27 research):
 *  · A station pass reads like the ISS: a steady non-blinking Venus-bright
 *    point, 3–6 min horizon-to-horizon, occasionally FLARING near culmination
 *    and sometimes fading out MID-SKY as it enters the planet's shadow.
 *  · Ambient satellites are much fainter slow movers (mag 5-ish).
 *  · Meteors: 0.2–1 s streaks subtending 5–20°, a few per ten minutes,
 *    the occasional bright one.
 *  · The fleet battle rides the SAME war clock as the space layer
 *    (BATTLE_PERIOD_S/BATTLE_LEN_S mirrored from SpaceModule — the
 *    cross-boundary mirror precedent), so the cruiser you can spot in the
 *    night sky IS the battle a pilot is flying in right now.
 */

export interface Vec3 { x: number; y: number; z: number }

/**
 * Deterministic [0,1) hash — FNV-1a + a murmur-style avalanche finalizer.
 * WHY the finalizer: bare FNV-1a is LAST-CHARACTER-WEAK — keys that differ
 * only in a trailing cycle digit (`me1041`, `me1042`, …) leave the final
 * xor one multiply from the output, so consecutive cycles cluster within
 * ~0.5% of each other (the verify harness caught meteors at 0% and flares
 * at 83% from exactly this). The xorshift-multiply rounds give full
 * avalanche on every input bit.
 */
export function skyH(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  h ^= h >>> 15; h = Math.imul(h, 0x2c1b3c6d);
  h ^= h >>> 12; h = Math.imul(h, 0x297a2d39);
  h ^= h >>> 15;
  return (h >>> 0) / 4294967296;
}

// ── The war clock — THE shared mirror, never a copy ──────────────────────────
// 2026-09-21: this file carried its own 1200 / 600 copy, which the ENGAGEMENT
// LAW (2026-09-17: a war every hour for 30 min) never reached — so a grounded
// player watched a 10-min sky battle every 20 min while the pilots above fought
// an hourly half-hour war. The sky reads the campaign mirror's clock directly.
export { BATTLE_PERIOD_S, BATTLE_LEN_S } from '@shared/space_campaign.js';
import { BATTLE_PERIOD_S, BATTLE_LEN_S, fortOf, type FortSpec } from '@shared/space_campaign.js';

// ── Station transit ──────────────────────────────────────────────────────────
export const STATION_PERIOD_S = 360; // a pass window opens every 6 min (user spec)…
export const STATION_PASS_S = 240;   // …and the crossing takes 4 min of it

// ── Meteors ──────────────────────────────────────────────────────────────────
export const METEOR_BUCKET_S = 16;   // one roll every 16 s…
export const METEOR_CHANCE = 0.32;   // …streaking ~1-2 times per minute

const norm = (x: number, y: number, z: number): Vec3 => {
  const l = Math.hypot(x, y, z) || 1;
  return { x: x / l, y: y / l, z: z / l };
};
const cross = (a: Vec3, b: Vec3): Vec3 =>
  ({ x: a.y * b.z - a.z * b.y, y: a.z * b.x - a.x * b.z, z: a.x * b.y - a.y * b.x });

/** A unit direction from azimuth (rad) + altitude (rad, 0=horizon .. π/2=zenith). */
export function skyDir(az: number, alt: number): Vec3 {
  const c = Math.cos(alt);
  return { x: Math.sin(az) * c, y: Math.sin(alt), z: Math.cos(az) * c };
}

/**
 * The great-circle position of a transit at phase p∈[0,1]: rises at one
 * horizon point, culminates at `peakAlt`, sets opposite. Built from a basis
 * (rise direction R, and the "up-ish" vector U ⊥ R in the plane): pos =
 * R·cos(θ) + U·sin(θ) with θ sweeping 0..π across the pass.
 */
export function transitPos(riseAz: number, peakAlt: number, p: number): Vec3 {
  const R = skyDir(riseAz, 0);
  // The plane's second basis vector: at θ=π/2 the object sits at peak altitude
  // on the meridian ⊥ the rise azimuth. Tilt = peakAlt away from the zenith.
  const zenith: Vec3 = { x: 0, y: 1, z: 0 };
  const side = norm(...(Object.values(cross(zenith, R)) as [number, number, number]));
  const U = norm(
    zenith.x * Math.sin(peakAlt) + side.x * Math.cos(peakAlt),
    zenith.y * Math.sin(peakAlt) + side.y * Math.cos(peakAlt),
    zenith.z * Math.sin(peakAlt) + side.z * Math.cos(peakAlt),
  );
  const th = p * Math.PI;
  return norm(
    R.x * Math.cos(th) + U.x * Math.sin(th),
    R.y * Math.cos(th) + U.y * Math.sin(th),
    R.z * Math.cos(th) + U.z * Math.sin(th),
  );
}

export interface MoverState {
  /** Current unit direction in the sky (y up). Below horizon ⇒ not drawn. */
  dir: Vec3;
  /** Linear brightness multiplier (0 = off). */
  bright: number;
}

/**
 * The STATION pass at wall-time `nowS`. Every STATION_PERIOD_S a pass window
 * opens; the crossing occupies the first STATION_PASS_S of it. Deterministic
 * per-cycle: rise azimuth, peak altitude, a possible culmination FLARE, and a
 * ~35% chance the station enters the planet's shadow and fades out mid-pass
 * (the authentic ISS detail). Returns bright=0 outside a pass.
 */
export function stationAt(nowS: number): MoverState {
  const cyc = Math.floor(nowS / STATION_PERIOD_S);
  const t = nowS - cyc * STATION_PERIOD_S;
  if (t > STATION_PASS_S) return { dir: { x: 0, y: -1, z: 0 }, bright: 0 };
  const p = t / STATION_PASS_S;
  const riseAz = skyH(`st-az${cyc}`) * Math.PI * 2;
  const peakAlt = 0.5 + skyH(`st-alt${cyc}`) * 0.9;        // 29°..80° culmination
  const dir = transitPos(riseAz, peakAlt, p);
  // Horizon fade at both ends; a shadow exit truncates ~35% of passes.
  let b = Math.min(1, p / 0.08) * Math.min(1, (1 - p) / 0.1);
  const shadow = skyH(`st-sh${cyc}`) < 0.35;
  if (shadow) {
    const exitP = 0.55 + skyH(`st-shp${cyc}`) * 0.25;      // fades out at 55-80% across
    b *= Math.max(0, Math.min(1, (exitP - p) / 0.06));     // a ~15 s dissolve into shadow
  }
  // Culmination flare (sun glint) on ~40% of passes: ×3 for ~8 s at mid-pass.
  if (skyH(`st-fl${cyc}`) < 0.4) {
    const d = Math.abs(p - 0.5) * STATION_PASS_S;          // seconds from culmination
    b *= 1 + 2.2 * Math.max(0, 1 - d / 8);
  }
  return { dir, bright: b * 2.6 };                          // Venus-bright vs a 1.0 star
}

/** Two faint AMBIENT satellites on slow offset transits — always in motion. */
export function ambientSatsAt(nowS: number): [MoverState, MoverState] {
  const out: MoverState[] = [];
  for (let i = 0; i < 2; i++) {
    const period = 300 + i * 80;
    const cyc = Math.floor(nowS / period);
    const p = (nowS - cyc * period) / period;
    const dir = transitPos(skyH(`sat${i}-${cyc}`) * Math.PI * 2, 0.35 + skyH(`sata${i}-${cyc}`) * 1.0, p);
    const b = Math.min(1, p / 0.1) * Math.min(1, (1 - p) / 0.1) * 0.22; // barely-there mag-5 dots
    out.push({ dir, bright: b });
  }
  return out as [MoverState, MoverState];
}

export interface MeteorState {
  /** Streak head direction (unit). */
  dir: Vec3;
  /** Streak direction of travel (unit, ⊥ dir): the tail extends opposite. */
  vel: Vec3;
  /** Streak angular length so far (radians, grows over the life). */
  len: number;
  /** 0 = off; peaks fast then decays with the train. */
  bright: number;
}

/**
 * The METEOR at `nowS` (at most one active; buckets roll every
 * METEOR_BUCKET_S). Life 0.25–0.9 s + a brief fading train; arc 5–20°.
 * Deterministic per bucket — every client sees the same streak.
 */
export function meteorAt(nowS: number): MeteorState {
  const off: MeteorState = { dir: { x: 0, y: -1, z: 0 }, vel: { x: 1, y: 0, z: 0 }, len: 0, bright: 0 };
  const cyc = Math.floor(nowS / METEOR_BUCKET_S);
  if (skyH(`me${cyc}`) >= METEOR_CHANCE) return off;
  const t0 = (cyc + skyH(`met0${cyc}`) * 0.55) * METEOR_BUCKET_S; // start somewhere in the bucket
  const dur = 0.25 + skyH(`med${cyc}`) * 0.65;                     // 0.25–0.9 s of flight
  const train = 0.5 + skyH(`metr${cyc}`) * 0.7;                    // afterglow train
  const t = nowS - t0;
  if (t < 0 || t > dur + train) return off;
  const start = skyDir(skyH(`meaz${cyc}`) * Math.PI * 2, 0.25 + skyH(`meal${cyc}`) * 1.0);
  const zen: Vec3 = { x: 0, y: 1, z: 0 };
  const side = cross(start, zen);
  const tilt = (skyH(`metl${cyc}`) - 0.5) * 1.6;
  const vel = norm(
    -zen.x + side.x * tilt, -zen.y + side.y * tilt, -zen.z + side.z * tilt, // mostly downward, tilted
  );
  const arc = (5 + skyH(`mear${cyc}`) * 15) * (Math.PI / 180);              // 5–20°
  const fly = Math.min(1, t / dur);
  const head = norm(start.x + vel.x * arc * fly, start.y + vel.y * arc * fly, start.z + vel.z * arc * fly);
  const decay = t <= dur ? 1 : Math.max(0, 1 - (t - dur) / train);
  const big = skyH(`meb${cyc}`);
  const bright = decay * (big < 0.12 ? 2.4 : 0.7 + big * 0.6);              // the rare fireball
  return { dir: head, vel, len: arc * fly, bright };
}

export interface BattleSky {
  /** 0 when no battle is visible; else the war-window envelope 0..1. */
  active: number;
  /** Where in the sky the engagement hangs (unit dir; low-ish). */
  dir: Vec3;
  /** The cruiser's heading tangent (unit, ⊥ dir) — the train trails behind. */
  tangent: Vec3;
  /** Seconds into the battle window (drives drift + flash hashes). */
  t: number;
  /** The war cycle id (salts the per-battle choreography). */
  cyc: number;
  /** The fort this cycle's war hangs over (the mirror's `fortOf`). */
  fort: string;
  /** Why the sky shows what it shows: `clock` (no row — the window alone), `row` (the campaign row's
   *  own end), `ceasefire` (a standing treaty — no war), `over` (the row ended early), `quiet` (no window). */
  source: 'clock' | 'row' | 'ceasefire' | 'over' | 'quiet';
  /** Seconds into the window at which the sky's war ENDS (the row's endsAt, else the window's). */
  endS: number;
  /** Space campaign L (2026-10-01): the direction rides the planet's rotation (THE DIURNAL FOLD) — false = the fixed
   *  landmark bearing (no observer given). */
  folded?: boolean;
}

/** Where the observer stands in the sky's day (space campaign L): the shared clock's time of day and the latitude. */
export interface SkyObserver { tod: number; latDeg: number }

/**
 * THE DIURNAL FOLD (space campaign L, 2026-10-01 — fleet_battle.md §5.13: "a projection of the sector through the
 * planet's rotation"). `d0` is where a body hangs at LOCAL MIDNIGHT (a world unit direction: x east, y up, z south);
 * its equatorial coordinates (hour angle H0, declination δ) are solved at latitude φ from the same spherical triangle
 * `sunDirectionAt` uses, and the planet's rotation carries it: H = H0 + 2π·tod. It rises in the east, culminates and sets
 * in the west on a true diurnal circle, and at midnight it is exactly `d0` — so the fort's landmark (the same fort's
 * war in the same part of the sky) holds at midnight. NOTE: Atlas's star sphere is pinned to the world (it does not
 * rotate); the sun, the moon, the planets — and now the war — move across it on this diurnal rule.
 */
export function diurnalFold(d0: Vec3, tod: number, latDeg: number): Vec3 {
  const phi = (Math.max(-89.9, Math.min(89.9, Number.isFinite(latDeg) ? latDeg : 0)) * Math.PI) / 180;
  const sp = Math.sin(phi), cp = Math.cos(phi);
  const east = d0.x, up = d0.y, north = -d0.z;
  const sd = Math.max(-1, Math.min(1, north * cp + up * sp));
  const cd = Math.sqrt(Math.max(0, 1 - sd * sd));
  const H0 = Math.atan2(-east, up * cp - north * sp);
  const H = H0 + 2 * Math.PI * (Number.isFinite(tod) ? tod : 0);
  const cH = Math.cos(H), sH = Math.sin(H);
  const e = -cd * sH;
  const n = sd * cp - cd * cH * sp;
  const u = sd * sp + cd * cH * cp;
  return norm(e, u, -n);
}

// ── THE WAR IN THE SKY, COUNTED (space campaign L, 2026-10-01; report §10.4 — "count-sensitive silhouettes,
//    instanced, ≤ 98"). Two walls of capitals facing each other along the heading, each wall's escorts ahead of it, the
//    gunship furball between — every count from the server's summary (`campaign_sky`), every place from a hash of the
//    cycle and the slot, so every client draws the same sky. Angles in radians about the battle's sky direction:
//    u along the heading tangent, v along the bitangent. ──
export interface SkyShip { side: 0 | 1; cls: 'capital' | 'frigate' | 'corvette' | 'gunship'; u: number; v: number; len: number; face: number }
// Sized to READ at sky scale (the probe's first picture: a 0.5° capital is a 0.06°-thin sliver — sub-pixel, it vanished):
// a capital spans 1.3° (the shader's old one-cruiser line was 1.3° long), a wall of ten 6°; the war patch ~13° × 7°.
export const SKY_LINE_SEP = 0.10;            // ≈ 5.7° between the walls
export const SKY_LEN = { capital: 0.022, frigate: 0.012, corvette: 0.0075, gunship: 0.004 } as const;
/** The thinnest a silhouette may be across (rad): ~1.5 px at 1080p / 60° — a thinner hull rasterises to nothing. */
export const SKY_MIN_THICK = 0.0022;

export function warSkyLayout(
  counts: { caps: readonly [number, number]; frigates: readonly [number, number]; corvettes: readonly [number, number]; gunships: readonly [number, number] },
  cyc: number, t: number,
): SkyShip[] {
  const out: SkyShip[] = [];
  const clampN = (n: number, max: number): number => Math.max(0, Math.min(max, Math.floor(Number.isFinite(n) ? n : 0)));
  let budget = 98;
  const push = (s: SkyShip): void => { if (budget > 0) { out.push(s); budget--; } };
  for (const side of [0, 1] as const) {
    const sgn = side === 0 ? -1 : 1;                       // side 0 on −u, facing +u (the enemy)
    const caps = clampN(counts.caps[side], 10);
    for (let k = 0; k < caps; k++) {
      const v = (k - (caps - 1) / 2) * 0.0118;
      push({ side, cls: 'capital', u: sgn * (SKY_LINE_SEP / 2 + (k % 2) * 0.006), v, len: SKY_LEN.capital, face: -sgn });
    }
    const fri = clampN(counts.frigates[side], 3);
    for (let k = 0; k < fri; k++) push({ side, cls: 'frigate', u: sgn * 0.034, v: (k - (fri - 1) / 2) * 0.02, len: SKY_LEN.frigate, face: -sgn });
    const cor = clampN(counts.corvettes[side], 6);
    for (let k = 0; k < cor; k++) push({ side, cls: 'corvette', u: sgn * 0.024, v: (k - (cor - 1) / 2) * 0.0105, len: SKY_LEN.corvette, face: -sgn });
  }
  for (const side of [0, 1] as const) {
    const gun = clampN(counts.gunships[side], 30);
    for (let k = 0; k < gun; k++) {
      // The furball: a hashed seat between the walls (biased to the own side), a slow orbit in t — every client agrees.
      const a = skyH(`wsg${cyc}:${side}:${k}`), b = skyH(`wsh${cyc}:${side}:${k}`), c = skyH(`wsi${cyc}:${side}:${k}`);
      const ph = c * Math.PI * 2 + t * (0.05 + a * 0.08);
      const u = (side === 0 ? -1 : 1) * 0.008 * a + Math.cos(ph) * 0.012;
      const v = (b - 0.5) * 0.08 + Math.sin(ph) * 0.008;
      push({ side, cls: 'gunship', u, v, len: SKY_LEN.gunship, face: Math.sin(ph + 1.3) >= 0 ? 1 : -1 });
    }
  }
  return out;
}

/** What the ground knows of the war beyond the clock (2026-09-22, "the sky reads neither civ_treaty nor
 *  the campaign row"): built from the replicated rows by `battleSkyInputs`, handed to `battleSkyAt`. */
export interface BattleSkyInputs {
  /** A standing ceasefire covers this cycle — no war opens, in space or in the sky. */
  ceasefire: boolean;
  /** The campaign row of THIS cycle, if replicated: its state and its end in seconds since the epoch. */
  campaign: { state: string; endsAtS: number } | null;
}
export const NO_BATTLE_INPUTS: BattleSkyInputs = { ceasefire: false, campaign: null };

/**
 * Reduce the replicated `civ_treaty` and `space_campaign` rows to what the sky needs for cycle `cyc`
 * (pure; the caller converts the SDK's bigints and Timestamps). Mirrors `SpaceModule.ceasefireStands`
 * and `campaignRow()`, so the ground and the sector agree on whether a war exists.
 */
export function battleSkyInputs(
  cyc: number,
  treaties: Iterable<{ state: string; fromCyc: number; untilCyc: number }>,
  campaigns: Iterable<{ cyc: number; state: string; endsAtS: number }>,
): BattleSkyInputs {
  let ceasefire = false;
  for (const tr of treaties) if (tr.state === 'standing' && tr.fromCyc <= cyc && tr.untilCyc > cyc) { ceasefire = true; break; }
  let campaign: BattleSkyInputs['campaign'] = null;
  for (const c of campaigns) if (c.cyc === cyc) { campaign = { state: c.state, endsAtS: c.endsAtS }; break; }
  return { ceasefire, campaign };
}

/** The sky band the engagement hangs in: 20°..49° — up in the sky, never overhead, never on the horizon. */
export const BATTLE_ALT_MIN = 0.35, BATTLE_ALT_MAX = 0.85;
/**
 * THE FORT'S BEARING. The war over a fort hangs where that fort lies from the Commons Reach's hub (the
 * sector origin — Aurel Prime sits 11 km from it; the forts a megametre out): azimuth = the fort's sector
 * bearing (`atan2(x, z)`, `skyDir`'s own convention), altitude = its elevation over the sector plane
 * (±15° covers all three) mapped into the sky band. So the same fort's war always hangs in the same part
 * of the sky and the three forts hang in three — a landmark, not a roll of the dice.
 */
export function fortSkyBearing(fort: FortSpec): { az: number; alt: number } {
  const [x, y, z] = fort.pos;
  const az = Math.atan2(x, z);
  const elev = Math.atan2(y, Math.hypot(x, z) || 1);                  // −0.21..+0.23 rad over the three forts
  const u = Math.max(0, Math.min(1, (elev + Math.PI / 12) / (Math.PI / 6)));   // ±15° → 0..1
  return { az, alt: BATTLE_ALT_MIN + u * (BATTLE_ALT_MAX - BATTLE_ALT_MIN) };
}

/**
 * The fleet battle AS SEEN FROM THE GROUND — the same wall-clock war window
 * SpaceModule uses to stage the sector battle (the shared campaign clock), so a
 * grounded player watching the night sky sees the war a pilot is fighting.
 * The engagement hangs at the FORT's bearing (`fortSkyBearing`), drifting slowly
 * along its heading like a distant orbital convoy. With `inputs` the sky also
 * reads what the sector reads: a ceasefire shows no war, and a campaign row's
 * own end (a retreat pulls it to now + 32 s) ends the sky's war with the field's.
 */
export function battleSkyAt(nowS: number, inputs: BattleSkyInputs = NO_BATTLE_INPUTS, observer: SkyObserver | null = null): BattleSky {
  const offd: Vec3 = { x: 0, y: -1, z: 0 };
  const cyc = Math.floor(nowS / BATTLE_PERIOD_S);
  const t = nowS - cyc * BATTLE_PERIOD_S;
  const fort = fortOf(cyc);
  const off = (source: BattleSky['source'], endS: number): BattleSky =>
    ({ active: 0, dir: offd, tangent: { x: 1, y: 0, z: 0 }, t: 0, cyc, fort: fort.key, source, endS });
  if (t > BATTLE_LEN_S) return off('quiet', BATTLE_LEN_S);
  if (inputs.ceasefire) return off('ceasefire', 0);                     // THE TREATY LAW — no war, no sky
  // The war's end inside the window: the row's own (a retreat's endsAt, the resolution) when it exists.
  let endS = BATTLE_LEN_S, source: BattleSky['source'] = 'clock';
  const c = inputs.campaign;
  if (c) {
    endS = Math.max(0, Math.min(BATTLE_LEN_S, c.endsAtS - cyc * BATTLE_PERIOD_S));
    source = 'row';
    if (c.state === 'resolved' && t >= endS) return off('over', endS); // the field is hulks — the sky is quiet
  }
  const { az, alt } = fortSkyBearing(fort);
  const dir0 = skyDir(az, alt);
  const zen: Vec3 = { x: 0, y: 1, z: 0 };
  const side = cross(dir0, zen);
  const l = Math.hypot(side.x, side.y, side.z) || 1;
  const tangent = { x: side.x / l, y: side.y / l, z: side.z / l };
  // The convoy drifts ~6° across the whole war window (30 min) — stately, orbital.
  const drift = (t / BATTLE_LEN_S - 0.5) * (6 * Math.PI / 180);
  const dirL = norm(dir0.x + tangent.x * drift, dir0.y + tangent.y * drift, dir0.z + tangent.z * drift);
  // Space campaign L: with an observer the war rides the planet's rotation (THE DIURNAL FOLD) — the landmark at midnight,
  // rising and setting through the night; the heading stays horizontal (⊥ the direction, in the horizon plane).
  const dir = observer ? diurnalFold(dirL, observer.tod, observer.latDeg) : dirL;
  let tan = tangent;
  if (observer) {
    const s2 = cross(dir, zen);
    const l2 = Math.hypot(s2.x, s2.y, s2.z);
    tan = l2 > 1e-6 ? { x: s2.x / l2, y: s2.y / l2, z: s2.z / l2 } : { x: 1, y: 0, z: 0 };
  }
  // 20 s in, 30 s out — the fade-out rides the finale (BATTLE_END_S 32) whether the window's or a retreat's.
  const active = Math.min(1, t / 20) * Math.max(0, Math.min(1, (endS - t) / 30));
  return { active, dir, tangent: tan, t, cyc, fort: fort.key, source, endS, folded: !!observer };
}

// ═══ PLANETS (2026-10-06): sys0's REAL worlds, as the Commons sees them ══════
// The ground's planets used to be four fictional orbits with hand-tuned discs (the "planet law" of 2026-09-08 capped
// them at the Moon after a 4.4° Vorune read as a floating structure over a town). They are now DERIVED from the sector
// a pilot flies (`space_sector.ts PLANETS` — sys0 is that hand-authored table, not `galaxy.systemSector`):
//  · THE COMMONS is PLANETS[0] (the ocean world under the launch ring, centre (0, −84.5 km, 0), r 78 km). The others
//    are Cindral, Vorune, Palewater and Cindral's moon Ashfall. sys0's sun has NO body: it stands at infinity along
//    `SYS0_SUN_DIR`. Nothing orbits (the sector's spheres only spin), so each world's place relative to the sun is fixed.
//  · THE FRAME. The sun is on the ecliptic by definition; the ecliptic's north pole is the sector's +Y tilted the least
//    that puts the sun on its equator (N = ŷ − (ŷ·S)S, normalised — the sector's heights become ecliptic latitude as
//    nearly as a sun standing 51° above the sector plane allows) and east is N × S. Each world's direction D from the
//    Commons' CENTRE (geocentric: the ground's diurnal model puts no observer on the sphere) gives its elongation east of
//    the sun λ = atan2(D·E, D·S) and its latitude β = asin(D·N).
//  · THE SKY. The sun's ecliptic longitude is the one whose declination the ground already DRAWS (`solarDeclinationDeg`);
//    a world's (λsun + λ, β) goes to RA / declination through the obliquity and to the horizon through the SAME transform
//    `sunDirectionAt` draws the sun with — the hour angle is the sun's less the RA difference, i.e. the old fold
//    `sunDirection(tod + Δ/2π)` made exact. Every step is a rotation, so the angle between a world and the drawn sun IS
//    its true elongation and between two worlds their true separation. East of the sun transits later (an evening
//    world) — the moon's sign; the fictional set used the mirror.
//  · SIZE. The true angular diameter is 2·asin(R/d) (a SPHERE's limb is tangent — 2·atan(R/d) is a flat disc facing
//    you; 26.21° vs 25.55° for Vorune). The sector is compressed ~100×, so the true sizes are huge (Vorune 26°, fifty
//    Moons); ONE global scale puts the largest at SKY_PLANET_MAX_DEG 1.0° — twice the Moon — and keeps the order and
//    every ratio exactly. A scaled disc under SKY_PLANET_POINT_DEG is drawn at that floor as a point, its surface
//    brightness diluted by the area ratio (the flux is kept).
//  · LIGHT. With the sun at infinity every world takes the same insolation (the 1/r² from the star is one number — the
//    Commons' own), so a disc's SURFACE luminance is its class albedo × the Moon's per unit albedo (`nits` — the Moon is
//    lit by the same sun at the same distance); the observer's 1/d² (× R²) reaches the eye through the drawn AREA — one
//    scale for all, so the drawn flux ratios are the true ratios (`flux`, ∝ albedo × solid angle × Lambert phase). The
//    renderer turns `nits` into scene units by THE PHYSICAL-EMISSIVE CONTRACT, the Moon's own path (photometry
//    `emissiveNits`). The phase is drawn: each sphere is lit from the drawn sun's direction (parallel rays — the sun is
//    at infinity), so its terminator shows the true sun–world–observer angle (π − elongation). Within
//    SKY_PLANET_GLARE_DEG of the sun a world is lost in the glare.
import { PLANETS, type PlanetSpec } from './space_sector.js';
import { SYS0_SUN_DIR } from './space/space_look.js';
import {
  sunDirectionAt, solarDeclinationDeg, sunObserver, EQUINOX_DOY, DAYS_PER_YEAR, OBLIQUITY_DEG, LAT_CENTER_DEG,
} from './lighting.js';
import { MOON_DISC_NITS } from './photometry.js';

/** The world the ground stands on (space_sector PLANETS[0] — "the ocean world you just left"). */
export const HOME_PLANET = 'The Commons';
/** The largest drawn disc (deg): twice the Moon's 0.5°. Nothing is ever drawn bigger. */
export const SKY_PLANET_MAX_DEG = 1.0;
/** A scaled disc smaller than this (deg, ~1 px at 1080p / 60°) is drawn at this size as a point, flux conserved. */
export const SKY_PLANET_POINT_DEG = 0.06;
/** The Moon's geometric albedo — MOON_DISC_NITS (its full disc at the top of the air) is that albedo's surface. */
export const MOON_ALBEDO = 0.12;
/** A sun-lit surface's luminance per unit geometric albedo at the top of the air (cd/m²): every sys0 world is lit by the
 *  Commons' own sun at the Commons' own distance (the sun is at infinity), so its lit face is this × its albedo. */
export const SURFACE_NITS_PER_ALBEDO = MOON_DISC_NITS / MOON_ALBEDO;
/** The glare: inside the first elongation (deg) a world is lost in the sun; past the second it is clear. */
export const SKY_PLANET_GLARE_DEG: readonly [number, number] = [8, 18];

export type PlanetClass = 'ocean' | 'desert' | 'gas' | 'ice' | 'rock';
/** Geometric albedo by class (Earth 0.37, Mars 0.17, Jupiter 0.52, Europa 0.67, the Moon 0.12) + the disc's tint. */
export const PLANET_CLASS: Readonly<Record<PlanetClass, { albedo: number; tint: { r: number; g: number; b: number } }>> = {
  ocean: { albedo: 0.37, tint: { r: 0.7, g: 0.85, b: 1.0 } },
  desert: { albedo: 0.17, tint: { r: 1.0, g: 0.62, b: 0.38 } },
  gas: { albedo: 0.52, tint: { r: 1.0, g: 0.85, b: 0.6 } },
  ice: { albedo: 0.67, tint: { r: 0.75, g: 0.88, b: 1.0 } },
  rock: { albedo: 0.12, tint: { r: 0.72, g: 0.7, b: 0.66 } },
};
/** The sector's face keys (textures/planets/<key>.png) → class. */
const CLASS_OF_TEX: Readonly<Record<string, PlanetClass>> = {
  planet_ocean: 'ocean', planet_desert: 'desert', planet_gas: 'gas', planet_ice: 'ice', planet_moon: 'rock',
};

const DEG = Math.PI / 180;
const clamp1 = (v: number): number => Math.max(-1, Math.min(1, v));
const smoothstepN = (a: number, b: number, x: number): number => { const t = Math.max(0, Math.min(1, (x - a) / (b - a))); return t * t * (3 - 2 * t); };
/** A sphere's solid angle (sr) from its angular diameter (rad). */
const solidAngle = (diamRad: number): number => 2 * Math.PI * (1 - Math.cos(diamRad / 2));
/** The Lambert sphere's phase law Φ(α) (1 at full, 0 at new). */
export function lambertPhase(alpha: number): number {
  const a = Math.max(0, Math.min(Math.PI, alpha));
  return (Math.sin(a) + (Math.PI - a) * Math.cos(a)) / Math.PI;
}

export interface SkyPlanetDef {
  /** As the sector names it. */
  name: string;
  /** Face key (textures/planets/<tex>.png) — the sector's own texture. */
  tex: string;
  cls: PlanetClass;
  albedo: number;
  tint: { r: number; g: number; b: number };
  /** Centre-to-centre distance from the Commons (m) and the world's radius (m). */
  distM: number;
  radiusM: number;
  /** True angular diameter 2·asin(R/d) (deg). */
  trueDeg: number;
  /** Drawn angular diameter (deg) = trueDeg × the global scale, floored at SKY_PLANET_POINT_DEG. */
  discDeg: number;
  /** Drawn at the floor (a point) — its true-scaled disc is under SKY_PLANET_POINT_DEG. */
  point: boolean;
  /** True elongation from the sun (rad, 0..π). */
  elong: number;
  /** Ecliptic longitude from the sun (rad, (−π, π], + = east = an evening world). */
  east: number;
  /** Ecliptic latitude (rad). */
  lat: number;
  /** Phase angle sun–world–observer (rad) = π − elongation (the sun is at infinity). */
  phase: number;
  /** Illuminated fraction of the disc (1 + cos α) / 2. */
  illum: number;
  /** Total flux relative to the brightest world (albedo × solid angle × Lambert phase; insolation equal for all). */
  flux: number;
  /** The lit face's luminance at the top of the air (cd/m²) — albedo × SURFACE_NITS_PER_ALBEDO; a point carries its
   *  area dilution so its flux is kept. The renderer takes it through `emissiveNits`. */
  nits: number;
  /** 1 clear of the sun … 0 lost in its glare. */
  glare: number;
}
export interface SkyPlanetTable {
  /** The ONE global scale (drawn ÷ true angular diameter). */
  scale: number;
  /** The world the sky is seen from. */
  home: string;
  /** The sector's unit sun direction the frame was built on. */
  sun: Vec3;
  planets: SkyPlanetDef[];
}

/**
 * The fixed geometry of every world in `bodies` as seen from `home`, with the sun at infinity along `sunDir`. Pure;
 * the default arguments are sys0 (`sys0SkyPlanets()` memoises that). A body inside the home world, a non-finite
 * position or radius, or a degenerate sun yields no entry rather than a NaN.
 */
export function skyPlanetTable(bodies: readonly PlanetSpec[] = PLANETS, sunDir: Vec3 = SYS0_SUN_DIR, home: string = HOME_PLANET): SkyPlanetTable {
  const hb = bodies.find((b) => b.name === home);
  const sl = Math.hypot(sunDir.x, sunDir.y, sunDir.z);
  const S = { x: sunDir.x / sl, y: sunDir.y / sl, z: sunDir.z / sl };
  const empty: SkyPlanetTable = { scale: 0, home, sun: S, planets: [] };
  if (!hb || !(sl > 1e-9) || !Number.isFinite(sl)) return empty;
  // The ecliptic pole: the sector's +Y, tilted the least that puts the sun on its equator (x̂ if the sun stands at ±Y).
  let N = { x: -S.y * S.x, y: 1 - S.y * S.y, z: -S.y * S.z };
  let nl = Math.hypot(N.x, N.y, N.z);
  if (nl < 1e-6) { N = { x: 1 - S.x * S.x, y: -S.x * S.y, z: -S.x * S.z }; nl = Math.hypot(N.x, N.y, N.z); }
  N = { x: N.x / nl, y: N.y / nl, z: N.z / nl };
  const E = cross(N, S); // east: (S, E, N) right-handed, so the sector → sky map is a rotation, never a mirror
  const [hx, hy, hz] = hb.pos;
  const raw: Array<Omit<SkyPlanetDef, 'discDeg' | 'point' | 'flux' | 'nits'> & { f: number }> = [];
  for (const b of bodies) {
    if (b === hb) continue;
    const vx = b.pos[0] - hx, vy = b.pos[1] - hy, vz = b.pos[2] - hz;
    const d = Math.hypot(vx, vy, vz);
    if (!Number.isFinite(d) || !Number.isFinite(b.radius) || !(b.radius > 0) || !(d > b.radius)) continue;
    const ux = vx / d, uy = vy / d, uz = vz / d;
    const ds = ux * S.x + uy * S.y + uz * S.z;
    const de = ux * E.x + uy * E.y + uz * E.z;
    const dn = ux * N.x + uy * N.y + uz * N.z;
    const trueRad = 2 * Math.asin(b.radius / d);
    const elong = Math.acos(clamp1(ds));
    const phase = Math.PI - elong;
    const key = /([^/]+)\.png$/.exec(b.tex)?.[1] ?? b.tex;
    const cls = CLASS_OF_TEX[key] ?? 'rock';
    const { albedo, tint } = PLANET_CLASS[cls];
    raw.push({
      name: b.name, tex: key, cls, albedo, tint, distM: d, radiusM: b.radius,
      trueDeg: trueRad / DEG, elong, east: Math.atan2(de, ds), lat: Math.asin(clamp1(dn)), phase,
      illum: (1 + Math.cos(phase)) / 2,
      glare: smoothstepN(SKY_PLANET_GLARE_DEG[0], SKY_PLANET_GLARE_DEG[1], elong / DEG),
      // Insolation 1 for all (the sun at infinity); the eye takes albedo × the solid angle × the phase law.
      f: albedo * solidAngle(trueRad) * lambertPhase(phase),
    });
  }
  if (!raw.length) return empty;
  const maxTrue = Math.max(...raw.map((p) => p.trueDeg));
  const maxF = Math.max(...raw.map((p) => p.f)) || 1;
  const scale = SKY_PLANET_MAX_DEG / maxTrue;
  const planets: SkyPlanetDef[] = raw.map(({ f, ...p }) => {
    const scaled = p.trueDeg * scale;
    const point = scaled < SKY_PLANET_POINT_DEG;
    const discDeg = point ? SKY_PLANET_POINT_DEG : scaled;
    const dilution = (scaled / discDeg) ** 2; // a point drawn bigger than its scaled disc keeps its flux
    return { ...p, discDeg, point, flux: f / maxF, nits: SURFACE_NITS_PER_ALBEDO * p.albedo * dilution };
  });
  return { scale, home, sun: S, planets };
}

let sys0Table: SkyPlanetTable | null = null;
/** sys0's worlds from the Commons (memoised — the sector is static). */
export function sys0SkyPlanets(): SkyPlanetTable { return (sys0Table ??= skyPlanetTable()); }

/** Where the observer stands in the sky's year: latitude (deg N) and day of year (the shared `sunObserver()` by default). */
export interface PlanetObserver { latDeg: number; dayOfYear: number }

/**
 * The unit sky direction (world axes: +X east, +Y up, +Z south) of a point at ecliptic longitude `east` (rad, from the
 * sun, + east) and latitude `lat` (rad) at time-of-day `tod`, for an observer at `latDeg` on `dayOfYear`: ecliptic →
 * equatorial through the obliquity, then the sun's own `sunDirectionAt` with the RA difference folded into the hour.
 * `east = lat = 0` is exactly the drawn sun.
 */
export function eclipticToSky(tod: number, latDeg: number, dayOfYear: number, east: number, lat: number): Vec3 {
  const t = Number.isFinite(tod) ? tod : 0;
  const phi = Number.isFinite(latDeg) ? Math.max(-89.9, Math.min(89.9, latDeg)) : LAT_CENTER_DEG;
  const doy = Number.isFinite(dayOfYear) ? dayOfYear : EQUINOX_DOY;
  const eps = OBLIQUITY_DEG * DEG, se = Math.sin(eps), ce = Math.cos(eps);
  // The sun's ecliptic longitude: the one whose declination the ground DRAWS (its season's quadrant).
  const season = (2 * Math.PI * (doy - EQUINOX_DOY)) / DAYS_PER_YEAR;
  const sinLs = clamp1(Math.sin(solarDeclinationDeg(doy) * DEG) / se);
  const cosLs = (Math.cos(season) >= 0 ? 1 : -1) * Math.sqrt(Math.max(0, 1 - sinLs * sinLs));
  const raSun = Math.atan2(sinLs * ce, cosLs);
  const lam = Math.atan2(sinLs, cosLs) + (Number.isFinite(east) ? east : 0);
  const b = Number.isFinite(lat) ? lat : 0;
  const cb = Math.cos(b), sb = Math.sin(b), cl = Math.cos(lam), sl = Math.sin(lam);
  const x = cb * cl, y = cb * sl * ce - sb * se, z = cb * sl * se + sb * ce;
  const ra = Math.atan2(y, x);
  // A later RA (east) is an earlier hour angle: the world transits (ra − raSun)/2π of a day after the sun.
  const d = sunDirectionAt(t + (raSun - ra) / (Math.PI * 2), phi, Math.asin(clamp1(z)) / DEG);
  const l = Math.hypot(d.x, d.y, d.z) || 1;
  return { x: d.x / l, y: d.y / l, z: d.z / l };
}

export interface PlanetSky {
  name: string;
  /** Unit sky direction NOW (may be below the horizon — caller culls on y). */
  dir: Vec3;
  /** True elongation from the sun (rad, 0..π) — the angle between `dir` and the drawn sun. */
  elong: number;
  /** Ecliptic longitude from the sun (rad, + east) and latitude (rad). */
  east: number;
  lat: number;
  /** Drawn and true angular diameters (deg); `point` = drawn at the floor. */
  discDeg: number;
  trueDeg: number;
  point: boolean;
  /** The lit face's luminance at the top of the air (cd/m²) and the total flux (brightest world = 1). */
  nits: number;
  flux: number;
  /** Phase angle (rad) and illuminated fraction. */
  phase: number;
  illum: number;
  /** 1 clear of the sun … 0 lost in its glare. */
  glare: number;
  tint: { r: number; g: number; b: number };
  tex: string;
}

/**
 * Every sys0 world's sky state at time-of-day `tod` for `observer` (default: the shared sun observer). Pure +
 * deterministic: the sector is static, so the same day, hour and place give the same sky on every client — the
 * worlds keep their places among the hours like the sun's (their elongations are fixed) and ride its season.
 */
export function planetsAt(tod: number, observer: PlanetObserver = sunObserver(), table: SkyPlanetTable = sys0SkyPlanets()): PlanetSky[] {
  return table.planets.map((p) => ({
    name: p.name,
    dir: eclipticToSky(tod, observer.latDeg, observer.dayOfYear, p.east, p.lat),
    elong: p.elong, east: p.east, lat: p.lat,
    discDeg: p.discDeg, trueDeg: p.trueDeg, point: p.point,
    nits: p.nits, flux: p.flux, phase: p.phase, illum: p.illum, glare: p.glare,
    tint: p.tint, tex: p.tex,
  }));
}

// ═══ THE MOON (2026-09-27, sky/post P2 — realism-additions §1.5) ═════════════
// The home world's OWN moon (Ashfall above is Cindral's): a synodic month of `MOON_SYNODIC_DAYS` world days
// (photometry). Its ELONGATION from the sun grows 0 → 2π over the month (0 new, π full); it transits elong/2π of a day
// AFTER the sun (a waxing moon is an evening moon — the real sign; the planets above share it since 2026-10-06), and
// it rides the ECLIPTIC's declination at its own longitude, so the winter full moon rides high and the summer one low,
// as they do. Pure + deterministic: every client, same moon.
/** The phase (0 new … 0.5 full … 1) at world day 0 — any value; the month runs from it. */
export const MOON_EPOCH_PHASE = 0.37;
export interface MoonSky {
  /** Unit sky direction NOW (may be below the horizon). */
  dir: Vec3;
  /** Elongation from the sun (rad, 0..2π; π = full). */
  elong: number;
  /** The phase angle α (deg; 0 = full, 180 = new) — the phase law's input. */
  alphaDeg: number;
  /** The month's phase 0..1 (0 new, 0.5 full). */
  phase: number;
}
let pinnedMoonPhase: number | null = null;
/** Probe / debug pin: hold the phase (0 new, 0.5 full); null releases. */
export function pinMoonPhase(phase: number | null): void {
  pinnedMoonPhase = phase === null || !Number.isFinite(phase) ? null : ((phase % 1) + 1) % 1;
}
export function moonPhasePinned(): number | null { return pinnedMoonPhase; }
/** The month's phase 0..1 on a (continuous) world-day count. */
export function moonPhaseAt(worldDays: number, synodicDays: number): number {
  if (pinnedMoonPhase !== null) return pinnedMoonPhase;
  const p = worldDays / Math.max(1, synodicDays) + MOON_EPOCH_PHASE;
  return ((p % 1) + 1) % 1;
}
/** The moon for an observer at `latDeg` on `dayOfYear` at time-of-day `tod`, `worldDays` into the calendar. */
export function moonAt(worldDays: number, tod: number, latDeg: number, dayOfYear: number, synodicDays: number): MoonSky {
  const phase = moonPhaseAt(worldDays, synodicDays);
  const elong = phase * Math.PI * 2;
  const lamSun = (2 * Math.PI * (dayOfYear - EQUINOX_DOY)) / DAYS_PER_YEAR;
  const declDeg = (Math.asin(Math.sin((OBLIQUITY_DEG * Math.PI) / 180) * Math.sin(lamSun + elong)) * 180) / Math.PI;
  const dir = sunDirectionAt(tod - elong / (Math.PI * 2), latDeg, declDeg);
  const elongDeg = (elong * 180) / Math.PI;
  return { dir: { x: dir.x, y: dir.y, z: dir.z }, elong, alphaDeg: Math.abs(180 - elongDeg), phase };
}

